import type { Routes } from '@angular/router';
import { AuthenticationGuard } from '@core/guards/authentication.guard';
import { AuthorizationGuard } from '@core/guards/authorization.guard';
import { LayoutComponent } from '@core/layout/layout.component';
import { VEHICLE_CATALOG_RESOURCES } from '@features/vehicle-catalog/vehicle-catalog.sections';

export const routes: Routes = [
  {
    path: '',
    canActivate: [AuthenticationGuard],
    component: LayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'forbidden',
        title: 'routes.forbidden',
        loadComponent: () => import('../shared/pages/forbidden/forbidden.component').then((c) => c.ForbiddenComponent),
      },
      {
        path: 'server-error',
        title: 'routes.serverError',
        loadComponent: () =>
          import('../shared/pages/server-error/server-error.component').then((c) => c.ServerErrorComponent),
      },
      {
        path: 'home',
        title: 'routes.home',
        loadComponent: () => import('../shared/pages/home/home.component').then((c) => c.HomeComponent),
      },
      {
        path: 'not-found',
        title: 'routes.notFound',
        loadComponent: () => import('../shared/pages/not-found/not-found.component').then((c) => c.NotFoundComponent),
      },
      {
        path: 'access-control',
        title: 'routes.accessControl.title',
        loadComponent: () =>
          import('../features/access-control/access-control.component').then((c) => c.AccessControlComponent),
        loadChildren: () => import('./access-control.routes').then((m) => m.accessControlRoutes),
      },
      {
        path: 'micare',
        title: 'routes.micare.title',
        canActivate: [AuthorizationGuard],
        data: { permission: 'micare:list' },
        loadChildren: () => import('./micare.routes').then((m) => m.micareRoutes),
      },
      {
        path: 'message-board',
        title: 'routes.messageBoard.title',
        loadChildren: () => import('./message-board.routes').then((m) => m.messageBoardRoutes),
      },
      {
        path: 'recalls',
        title: 'routes.recalls.title',
        canActivate: [AuthorizationGuard],
        data: { permission: 'recall:list' },
        loadChildren: () => import('./recalls.routes').then((m) => m.recallsRoutes),
      },
      /*
        Top-level like IMD programs rather than a Vehicle Catalog page: an offer is a dated,
        province-scoped promotion, and mmscan-api keeps `offer` outside the vehicle-catalog module
        for the same reason (`/v1/offers`).
      */
      {
        path: 'offers',
        title: 'routes.offers.title',
        canActivate: [AuthorizationGuard],
        data: { permission: 'vehicle_offer:list' },
        loadChildren: () => import('./offers.routes').then((m) => m.offersRoutes),
      },
      /*
        `loadChildren` alone, like recalls: IMD programs are a flat list and a detail page, with no
        sidebar of their own. The six child collections live in tabs on the detail page rather than
        as sibling routes, because none of them is meaningful outside its parent program.
      */
      {
        path: 'imd-programs',
        title: 'routes.imdPrograms.title',
        canActivate: [AuthorizationGuard],
        data: { permission: 'imd_program:list' },
        loadChildren: () => import('./imd-programs.routes').then((m) => m.imdProgramRoutes),
      },
      /*
        `loadComponent` *and* `loadChildren`, the shape access-control uses: the component is the
        shell that renders the sidebar, and the children render into its `router-outlet`. Recalls and
        micare use `loadChildren` alone because neither has a sidebar.

        Gated on listing any one of its pages rather than a `vehicle_catalog` resource — no such
        Cerbos kind exists; the module's policies are per sub-resource. Each page guards itself.
      */
      {
        path: 'vehicle-catalog',
        title: 'routes.vehicleCatalog.title',
        canActivate: [AuthorizationGuard],
        data: {
          permission: VEHICLE_CATALOG_RESOURCES.map((resource) => `${resource}:list`),
          requireAll: false,
        },
        loadComponent: () =>
          import('../features/vehicle-catalog/vehicle-catalog.component').then((c) => c.VehicleCatalogComponent),
        loadChildren: () => import('./vehicle-catalog.routes').then((m) => m.vehicleCatalogRoutes),
      },
    ],
  },
  {
    path: 'sign-in',
    title: 'routes.signIn',
    loadComponent: () => import('../shared/pages/sign-in/sign-in.component').then((c) => c.SignInComponent),
  },
  {
    path: '**',
    redirectTo: 'not-found',
  },
];
